// src/shell/useStaticShellMenu.ts
//
// Synthetic replacement for `useGetMenuQuery` that the shared
// `@levelup/ui-shell` SuiteShell calls for its persistent left rail.
//
// ── VISIBILITY IS CENTRAL CAPABILITIES, NOT THE CRM ROLE ─────────────
// Every access-controlled entry now resolves through `isSidebarRowVisible`
// — the SAME rule the Roles & Access editor and its preview use. One
// mapping, three consumers, so they cannot disagree: switching Pipeline
// off for one member genuinely removes Pipeline from that member's rail.
//
// This replaces `MENU_KEY_ROLE_ACCESS`, which gated 22 of 23 entries on the
// CRM in-app role. Under it, two members with the same role saw the same
// menu no matter what an admin had granted, so an entry an admin had
// explicitly switched OFF still appeared.
//
// A workspace role is NOT an alternative allow path. An Admin without the
// deals capability does not see Pipeline. Central decides.
//
// ── UNKNOWN IS NOT ALLOWED ───────────────────────────────────────────
// `useCentralContext()` returns null until boot is ready, and a null
// context yields no capabilities — so controlled entries stay hidden while
// the context resolves and if Central is unreachable. An outage must not
// become elevated navigation, and nothing may flash in and then vanish.
//
// ── WHAT THIS IS NOT ─────────────────────────────────────────────────
// Navigation, not authorization. Hiding a menu entry does not protect the
// route behind it; the CRM server does that, and the enforcement badges in
// Roles & Access report honestly where it does not yet.

import { useMemo } from 'react';
import { useCentralContext } from '../services/centralContext';
import type { CrmRoleKey } from '@levelup/crm-roles';
import type { MenuKey } from '../types/api';
import {
  buildSidebarAccessRows,
  isSidebarRowVisible,
  scopesFromCapabilities,
} from '../config/accessCatalog';

/**
 * RETAINED FOR REFERENCE ONLY — nothing reads this at runtime.
 *
 * The old allow-list: a CRM in-app role could reveal an entry regardless of
 * what Central had granted. Kept so the previous behaviour stays legible in
 * review, and deliberately unused so it cannot come back as a second allow
 * path. A test asserts a role never reveals a capability-controlled entry.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MENU_KEY_ROLE_ACCESS: Partial<Record<MenuKey, ReadonlySet<CrmRoleKey>>> = {
  // Every key here is now capability-driven; this map is unused. `emails` is
  // kept verbatim although the entry itself is gone — this records what the
  // rule USED to be, and editing history to match the present would defeat
  // the point of keeping it.
  emails: new Set(['owner', 'administrator', 'coordinator', 'agent']),
  phone: new Set(['owner', 'administrator', 'coordinator', 'agent']),
  forms: new Set(['owner', 'administrator', 'coordinator']),
  channels: new Set(['owner', 'administrator', 'coordinator']),
  offerings: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  payments: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  businessProfile: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  portfolio: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  recruitment: new Set(['owner', 'administrator', 'coordinator']),
  marketplace: new Set(['owner', 'administrator', 'coordinator']),
  settings: new Set(['owner', 'administrator']),
};

/**
 * EXPORTED so Roles & Access can present module permissions in the same
 * language and the same order the admin sees in the app. Export only —
 * the list and this hook's visibility rules are unchanged.
 */
export const SIDEBAR_ITEMS: ReadonlyArray<{ menuKey: MenuKey; label: string }> = [
  { menuKey: 'home', label: 'Home' },
  // ONE destination for people AND organizations. `/contacts` renders the
  // People tab and `/contacts/companies` the Companies tab, so both — and both
  // detail surfaces — sit under the `/contacts` prefix and light exactly this
  // entry. Companies was a separate row until this pass; it had its own live
  // route the whole time, which is why the deep link is preserved by redirect
  // rather than dropped.
  { menuKey: 'contacts', label: 'Contacts' },
  { menuKey: 'tasks', label: 'Tasks' },
  { menuKey: 'pipeline', label: 'Pipeline' },
  // ONE destination for communications. "Emails" used to sit directly below,
  // pointing at `/messages?medium=email` — the same page under a query string,
  // which is why BOTH entries rendered active on the email-filtered inbox.
  // Email is a medium filter inside this surface, not a sibling of it.
  { menuKey: 'messages', label: 'Messages' },
  { menuKey: 'phone', label: 'Phone' },
  { menuKey: 'schedule', label: 'Schedule' },
  { menuKey: 'circles', label: 'Circles' },
  { menuKey: 'referrals', label: 'Referrals' },
  // Labelled "Performance" across every nav surface — the entry shows
  // accountability and time, not a roster. Display text only; visibility
  // and permissions are untouched.
  { menuKey: 'team', label: 'Performance' },
  { menuKey: 'forms', label: 'Forms' },
  { menuKey: 'files', label: 'Files' },
  { menuKey: 'projects', label: 'Projects' },
  { menuKey: 'offerings', label: 'Offerings' },
  { menuKey: 'payments', label: 'Payments' },
  { menuKey: 'businessProfile', label: 'Business Profile' },
  { menuKey: 'portfolio', label: 'Portfolio' },
  { menuKey: 'recruitment', label: 'Recruitment' },
  { menuKey: 'marketplace', label: 'Marketplace' },
  { menuKey: 'channels', label: 'Channels' },
  { menuKey: 'settings', label: 'Settings' },
];

interface ShellMenuItem {
  menuKey: string;
  label: string;
  order: number;
  isVisible: boolean;
  permissions: {
    canView: boolean;
    canCreate?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
  };
}

interface ShellMenuResult {
  data?: { menu: ShellMenuItem[] };
  isLoading: boolean;
  error?: unknown;
}

/**
 * Test-only views of the two registries above. Exported so regression tests can
 * pin sidebar membership and the deliberate absence of a role gate without
 * rendering the whole shell. Not used by application code.
 */
export const MENU_KEYS_FOR_TEST: readonly string[] = SIDEBAR_ITEMS.map((i) => i.menuKey);
export const MENU_KEY_ROLE_ACCESS_FOR_TEST = MENU_KEY_ROLE_ACCESS;

/**
 * RETAINED FOR REFERENCE ONLY — nothing reads this at runtime either.
 *
 * The interim registry from the Messages pass, when exactly one key had a
 * Central contract and the rest were still role-gated. Every entry now
 * resolves through the shared catalog, so a per-key registry would be a
 * second mapping to keep in step with the first.
 *
 * Messages keeps its specific contract: `crm:messages:read` reveals it, and
 * `crm:conversations` deliberately does not. That lives in the catalog as
 * `SIDEBAR_ENTRY_SCOPES` so the editor and preview honour it too.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MENU_KEY_CAPABILITY: Partial<Record<MenuKey, { domain: string; action: string }>> = {
  messages: { domain: 'messages', action: 'read' },
};

export const MENU_KEY_CAPABILITY_FOR_TEST = MENU_KEY_CAPABILITY;

export function useGetMenuQuery(
  _workspaceId: string,
  options?: { skip?: boolean },
): ShellMenuResult {
  // Null until boot is ready, which is what makes this fail closed: no
  // context means no capabilities, so controlled entries stay hidden rather
  // than falling back to a role.
  const context = useCentralContext();

  return useMemo(() => {
    if (options?.skip) {
      return { isLoading: false };
    }

    const granted = scopesFromCapabilities(
      (context?.currentWorkspace as { capabilities?: unknown } | undefined)?.capabilities,
    );

    const visible = new Set(
      buildSidebarAccessRows()
        .filter((row) => isSidebarRowVisible(row, granted))
        .map((row) => row.key),
    );

    const menu: ShellMenuItem[] = SIDEBAR_ITEMS
      .filter((item) => visible.has(item.menuKey))
      .map((item, index) => ({
        menuKey: item.menuKey,
        label: item.label,
        order: index,
        isVisible: true,
        permissions: { canView: true },
      }));

    return { data: { menu }, isLoading: false };
  }, [options?.skip, context]);
}
